import { useEffect, useRef, useState } from "react";
import {
  BarChart3,
  CalendarDays,
  ChevronDown,
  CircleDot,
  Crosshair,
  Gem,
  GitFork,
  Menu,
  ScrollText,
  Users,
  VenetianMask,
  X,
} from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";
import PlayerFinder from "./PlayerFinder";
import "./styles.css";

const DoubleXpIcon = () => (
  <span className="double-xp-icon">
    <strong>2</strong>
    <span>XP</span>
  </span>
);

const GqIcon = () => <span className="gq-icon">GQ</span>;

const LeaderboardIcon = () => (
  <svg
    className="leaderboard-icon"
    viewBox="0 0 22 22"
    fill="none"
    aria-hidden="true"
  >
    <path d="M1.75 19V11.25H7.5V19M7.5 19V5.75H14.5V19M14.5 19V13.25H20.25V19M1.75 19H20.25" />
    <text x="4.62" y="16.25">
      2
    </text>
    <text x="11" y="10.7">
      1
    </text>
    <text x="17.38" y="17.75">
      3
    </text>
  </svg>
);

const MobIcon = () => (
  <svg className="mob-icon" viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <path d="M5.25 8.2 2.6 4.35l.75 5.6M16.75 8.2l2.65-3.85-.75 5.6" />
    <path d="M4.15 10.1c0-4.05 2.85-6.55 6.85-6.55s6.85 2.5 6.85 6.55v3.15c0 3.05-2.45 5.5-5.5 5.5h-2.7a5.5 5.5 0 0 1-5.5-5.5V10.1Z" />
    <path d="m7 11.35 2.25 1M15 11.35l-2.25 1M8.45 15.4h5.1" />
  </svg>
);

const statsLinks = [
  {
    to: "/leaderboards/players",
    label: "PK Leaderboards",
    icon: LeaderboardIcon,
  },
  {
    to: "/feed",
    label: "PK Feed",
    icon: Crosshair,
  },
  {
    to: "/epic-leaderboard",
    label: "Epic Leaderboard",
    icon: LeaderboardIcon,
  },
  {
    to: "/epics-dashboard",
    label: "Epics Dashboard",
    icon: BarChart3,
  },
  {
    to: "/gquests",
    label: "Global Quests",
    icon: GqIcon,
    isNew: true,
  },
  {
    to: "/doubles",
    label: "Doubles",
    icon: DoubleXpIcon,
    isNew: true,
  },
  {
    to: "/player",
    label: "Player Stats",
    icon: Users,
  },
];

const gameDataLinks = [
  {
    to: "/gquest-mobs",
    label: "GQ Mobs",
    icon: MobIcon,
    isNew: true,
  },
  {
    to: "/race-modifiers",
    label: "Race Modifiers",
    icon: CircleDot,
  },
  {
    to: "/races",
    label: "Races",
    icon: VenetianMask,
  },
  {
    to: "/subclasses",
    label: "Subclasses",
    icon: GitFork,
  },
];

const explorePaths = [...statsLinks, ...gameDataLinks].map(({ to }) => to);

const ExploreLink = ({ item, onClick }) => {
  const Icon = item.icon;
  return (
    <NavLink to={item.to} className="explore-link" onClick={onClick}>
      <span className="explore-link-icon" aria-hidden="true">
        <Icon size={17} strokeWidth={1.8} />
      </span>
      <span className="explore-link-copy">
        <span className="explore-link-title">
          {item.label}
          {item.isNew && (
            <span
              className="nav-new-badge"
              role="img"
              aria-label="New"
              title="New"
            />
          )}
        </span>
      </span>
    </NavLink>
  );
};

function Header() {
  const location = useLocation();
  const [exploreOpen, setExploreOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const headerRef = useRef(null);
  const exploreButtonRef = useRef(null);
  const mobileButtonRef = useRef(null);
  const exploreActive = explorePaths.some((path) =>
    location.pathname.startsWith(path),
  );

  const closeMenus = () => {
    setExploreOpen(false);
    setMobileOpen(false);
  };

  useEffect(() => {
    setExploreOpen(false);
    setMobileOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!exploreOpen && !mobileOpen) return undefined;
    const handlePointerDown = (event) => {
      if (!headerRef.current?.contains(event.target)) closeMenus();
    };
    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      const returnFocus = mobileOpen
        ? mobileButtonRef.current
        : exploreButtonRef.current;
      closeMenus();
      returnFocus?.focus();
    };
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [exploreOpen, mobileOpen]);

  return (
    <header className="site-header" ref={headerRef}>
      <div className="site-header-bar">
        <div className="site-brand-group">
          <NavLink to="/" className="site-brand" aria-label="Aardstats home">
            <span className="site-brand-name">Aardstats</span>
          </NavLink>
          <a
            className="site-brand-external"
            href="https://www.aardwolf.com"
            target="_blank"
            rel="noreferrer"
          >
            aardwolf.com
          </a>
        </div>

        <nav className="desktop-nav" aria-label="Primary navigation">
          <NavLink to="/" end className="header-nav-link">
            Dashboard
          </NavLink>
          <button
            ref={exploreButtonRef}
            type="button"
            className={`header-nav-link explore-trigger${exploreActive ? " route-active" : ""}`}
            aria-expanded={exploreOpen}
            aria-controls="explore-menu"
            aria-haspopup="true"
            onClick={() => setExploreOpen((open) => !open)}
          >
            Explore
            <ChevronDown
              size={14}
              strokeWidth={2}
              className="explore-chevron"
              aria-hidden="true"
            />
          </button>
          <NavLink to="/epics" className="header-nav-link">
            Calendar
          </NavLink>
          <NavLink to="/market" className="header-nav-link">
            Market
          </NavLink>
          <NavLink to="/changelog" className="header-nav-link">
            Changelog
          </NavLink>
        </nav>

        <div className="header-actions">
          <PlayerFinder />
          <button
            ref={mobileButtonRef}
            type="button"
            className="mobile-menu-trigger"
            aria-label={mobileOpen ? "Close navigation" : "Open navigation"}
            aria-expanded={mobileOpen}
            aria-controls="mobile-navigation"
            onClick={() => setMobileOpen((open) => !open)}
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <div
        id="explore-menu"
        className={`explore-menu${exploreOpen ? " is-open" : ""}`}
        aria-hidden={!exploreOpen}
      >
        <div className="explore-menu-inner">
          <section
            className="explore-section"
            aria-labelledby="numbers-heading"
          >
            <div className="explore-section-heading" id="numbers-heading">
              Numbers
            </div>
            <div className="explore-grid explore-grid-stats">
              {statsLinks.map((item) => (
                <ExploreLink key={item.to} item={item} onClick={closeMenus} />
              ))}
            </div>
          </section>
          <div className="explore-divider" aria-hidden="true" />
          <section className="explore-section" aria-labelledby="data-heading">
            <div className="explore-section-heading" id="data-heading">
              Game data
            </div>
            <div className="explore-grid">
              {gameDataLinks.map((item) => (
                <ExploreLink key={item.to} item={item} onClick={closeMenus} />
              ))}
            </div>
          </section>
        </div>
      </div>

      <nav
        id="mobile-navigation"
        className={`mobile-navigation${mobileOpen ? " is-open" : ""}`}
        aria-label="Mobile navigation"
        aria-hidden={!mobileOpen}
      >
        <div className="mobile-primary-links">
          <NavLink to="/" end className="mobile-nav-link">
            Dashboard
          </NavLink>
          <NavLink to="/epics" className="mobile-nav-link">
            <CalendarDays size={17} aria-hidden="true" /> Calendar
          </NavLink>
          <NavLink to="/market" className="mobile-nav-link">
            <Gem size={17} aria-hidden="true" /> Market
          </NavLink>
          <NavLink to="/changelog" className="mobile-nav-link">
            <ScrollText size={17} aria-hidden="true" /> Changelog
          </NavLink>
        </div>
        <div className="mobile-explore-label">Numbers</div>
        <div className="mobile-explore-grid">
          {statsLinks.map((item) => (
            <ExploreLink key={item.to} item={item} onClick={closeMenus} />
          ))}
        </div>
        <div className="mobile-explore-label">Game data</div>
        <div className="mobile-explore-grid">
          {gameDataLinks.map((item) => (
            <ExploreLink key={item.to} item={item} onClick={closeMenus} />
          ))}
        </div>
      </nav>
    </header>
  );
}

export default Header;
